import clsx from 'clsx'
import { defineComponent, computed } from 'vue'

import { type JSX } from 'vue/jsx-runtime'

import VButton from '@/components/common/button/VButton'
import VIcon from '@/components/common/VIcon'
import useAuth from '@/composables/auth/useAuth'
import useLocalization from '@/composables/localization/useLocalization'
import useBookingView from '@/composables/views/booking/useBookingView'
import { type AdmissionRoom } from '@/types/Room'
import { getLocalizationName } from '@/utils/localization'
import { stringToCurrencyFormat } from '@/utils/number'

interface Props {
  item: AdmissionRoom
}

export default defineComponent<Props>((props) => {
  const auth = useAuth()
  const { localeTexts } = useLocalization()
  const {
    admissionRoomSelected,
    setAdmissionRoomSelected
  } = useBookingView()

  // #region Getters

  const featureImages = computed((): string => {
    return props.item.feature_images ?? '/assets/images/default-room.jpg'
  })

  const roomName = computed((): string | null => {
    return getLocalizationName(props.item, auth.userProfile!.language_preference)
  })

  const roomRate = computed((): string => {
    return `${stringToCurrencyFormat(props.item.price.toString())} ${localeTexts.value['currency-unit-baht-text']}`
  })

  const nursingFee = computed((): string => {
    if (props.item.nurse_fee === null) {
      return '-'
    }

    return `${stringToCurrencyFormat(props.item.nurse_fee.toString())} ${localeTexts.value['currency-unit-baht-text']}`
  })

  const serviceFee = computed((): string => {
    return `${stringToCurrencyFormat(props.item.service_fee.toString())} ${localeTexts.value['currency-unit-baht-text']}`
  })

  const meal = computed((): string => {
    if (props.item.meal_fee === null) {
      return '-'
    }

    return `${stringToCurrencyFormat(props.item.meal_fee.toString())} ${localeTexts.value['currency-unit-baht-text']}`
  })

  const totalPrice = computed((): string => {
    return `${stringToCurrencyFormat(props.item.total_price.toString())} ${localeTexts.value['currency-unit-baht-text']}`
  })

  const selectBtnText = computed((): string => {
    return admissionRoomSelected.value?.id === props.item.id ? localeTexts.value['create-booking-step1-room-unselected-btn'] : localeTexts.value['create-booking-step1-room-select-btn']
  })

  const selectBtnColor = computed((): string => {
    return admissionRoomSelected.value?.id === props.item.id ? clsx('btn-success') : clsx('btn-gray')
  })

  const selectBtnIcon = computed((): JSX.Element | null => {
    if (admissionRoomSelected.value?.id === props.item.id) {
      return (
        <VIcon
          class='text-[18px]'
          icon='material-symbols:check'
        />
      )
    }

    return null
  })

  // #endregion

  return () => (
    <div
      t-data='booking-admission-room-item'
      class='flex h-full flex-col gap-6 tablet:flex-row'
    >
      <div class='flex-1'>
        <img
          t-data='booking-admission-room-item-img'
          class='aspect-[3/2] h-auto w-full object-cover desktop:aspect-auto desktop:h-full'
          src={featureImages.value}
        />
      </div>
      <div class='flex flex-1 flex-col'>
        <div class='flex-initial tablet:flex-1'>
          <div
            t-data='booking-admission-room-item-name'
            class='text-subtitle-1'
          >{roomName.value}
          </div>
          <div class='mt-2'>
            <div class='flex text-body-1 text-neutral-500'>
              <div class='flex-1'>{localeTexts.value['create-booking-step1-room-rate-label']}</div>
              <div
                t-data='booking-admission-room-item-room-fee'
                class='flex-none'
              >{roomRate.value}
              </div>
            </div>
            <div class='flex text-body-1 text-neutral-500'>
              <div class='flex-1'>{localeTexts.value['create-booking-step1-room-nursingfee-label']}</div>
              <div
                t-data='booking-admission-room-item-nursing-fee'
                class='flex-none'
              >{nursingFee.value}
              </div>
            </div>
            <div class='flex text-body-1 text-neutral-500'>
              <div class='flex-1'>{localeTexts.value['create-booking-step1-room-meal-fee-label']}</div>
              <div
                t-data='booking-admission-room-item-meal-fee'
                class='flex-none'
              >{meal.value}
              </div>
            </div>
            <div class='flex text-body-1 text-neutral-500'>
              <div class='flex-1'>{localeTexts.value['create-booking-step1-service-fee-label']}</div>
              <div
                t-data='booking-admission-room-item-service-fee'
                class='flex-none'
              >{serviceFee.value}
              </div>
            </div>
          </div>
          <div class='mt-2 flex text-subtitle-1'>
            <div class='flex-1'>{localeTexts.value['create-booking-step1-total-price-label']}</div>
            <div
              t-data='booking-admission-room-item-total-price'
              class='flex-none'
            >{totalPrice.value}
            </div>
          </div>
        </div>
        <div class='mt-6 flex justify-end tablet:mt-0'>
          <VButton
            t-data='booking-admission-room-item-select-btn'
            class='btn-small'
            color={selectBtnColor.value}
            onClick={() => { setAdmissionRoomSelected(props.item) }}
          >
            <span>{selectBtnText.value}</span>
            {selectBtnIcon.value}
          </VButton>
        </div>
      </div>
    </div>
  )
}, {
  name: 'AdmissionRoomItem',
  props: ['item']
})
